import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, TemplateResult, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';

import {
  ConfigurationPrintOptionDto2,
  PrintOptionDto,
  SelectedPrintOptionDto,
} from '@/api-sdk';
import { FgDialog } from '@/components';
import '@/components/fg-dialog';
import { iconChevronLeft } from '@/components/icons';
import {
  GlobalComponentType,
  PersonalisationType as PersonalistaionContextType,
  UploadServiceContext,
  global,
  personalisation,
  uploadService,
} from '@/context';
import {
  FLOW_LOGO,
  FLOW_LOGO_FULL_COLOR,
  FLOW_LOGO_TEXT,
  FLOW_LOGO_TEXT_FULL_COLOR,
  FLOW_TEXT,
  FLOW_TEXT_FULL_COLOR,
  STEP_DONE,
} from '@/data';
import { BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, buttonStyles } from '@/styles';
import {
  PersonalisationFlow,
  PersonalisationStep,
  PersonalisationStepId,
  PersonalisationType,
} from '@/types';

import './step/fg-personalisation-choose-colors-step';
import './step/fg-personalisation-choose-type';
import './step/fg-personalisation-color-option-step';
import './step/fg-personalisation-error';
import './step/fg-personalisation-file';
import './step/fg-personalisation-step-done';
import './step/fg-personalisation-text-step';
import './step/fg-personalisation-uploading-step';
import './step/fg-upload-file';

/**
 * @element fg-personalisation
 */
@customElement('fg-personalisation')
@localized()
export class FgPersonalisation extends LitElement {
  private fgDialogRef: Ref<FgDialog> = createRef();

  @property({ type: Object })
  printOption?:
    | PrintOptionDto
    | ConfigurationPrintOptionDto2
    | SelectedPrintOptionDto;

  @property({ type: Boolean })
  hasColorChoice? = true;

  @state()
  private stepIndex = 0;

  @state()
  private flow: PersonalisationFlow = FLOW_LOGO_TEXT;

  @state()
  private fileName?: string;

  @state()
  private file?: File;

  @state()
  private stepSkipped = false;

  @state()
  private errorMessage?: string;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: uploadService, subscribe: true })
  uploadService: UploadServiceContext = undefined;

  @consume({ context: personalisation.context, subscribe: true })
  personalisation!: PersonalistaionContextType[];

  public openDialog() {
    this.stepIndex = 0;
    this.fgDialogRef.value?.open();
  }

  public closeDialog() {
    this.fgDialogRef.value?.close();
  }

  private onSelectFlow(event: CustomEvent) {
    const { type } = event.detail;

    switch (type) {
      case PersonalisationType.LOGO:
        if (this.hasColorChoice) {
          this.flow = FLOW_LOGO;
          break;
        }
        this.flow = FLOW_LOGO_FULL_COLOR;
        break;
      case PersonalisationType.LOGO_TEXT:
        if (this.hasColorChoice) {
          this.flow = FLOW_LOGO_TEXT;
          break;
        }
        this.flow = FLOW_LOGO_TEXT_FULL_COLOR;
        break;
      case PersonalisationType.TEXT:
        if (this.hasColorChoice) {
          this.flow = FLOW_TEXT;
          break;
        }
        this.flow = FLOW_TEXT_FULL_COLOR;
        break;
    }

    if (this.printOption) {
      personalisation.providerUpdateEvent(this, {
        printOptionCode: this.printOption.code,
        type,
        colors: [],
        textPersonalization: null,
        logoUuid: null,
      });
    }

    this.stepIndex = 1;
  }

  private goBack() {
    //Prevent uploading to be fired again when going back from color source step
    if (this.getCurrentStep().id === PersonalisationStepId.COLORS_SOURCE) {
      this.stepIndex = PersonalisationStepId.UPLOAD_SELECT_FILE;
    }
    if (this.stepIndex > 0 || !this.stepSkipped) {
      this.stepIndex--;
    }
    // Check if a step is skipped and go back 2 steps
    if (this.stepSkipped) {
      this.stepIndex = this.stepIndex - 2;
      // Reset the step skipped state after going back
      this.stepSkipped = false;
    }
  }

  private goFurther() {
    if (this.flow !== undefined && this.stepIndex < this.flow.length - 1) {
      this.stepIndex++;
    }
  }

  private getCurrentStep(): PersonalisationStep {
    return this.flow[this.stepIndex];
  }

  private getStepTitle(): string {
    return t(this.getCurrentStep().titleTranslationKey);
  }

  private getBackButton(): TemplateResult {
    if (
      this.stepIndex === 0 ||
      this.getCurrentStep().id === PersonalisationStepId.DONE
    ) {
      return html``;
    }

    return html`
      <button class="previous-button" @click=${() => this.goBack()}>
        <div class="previous-icon">${iconChevronLeft}</div>
        ${t('actions.back')}
      </button>
    `;
  }

  private goToUpload(event: CustomEvent) {
    const { name: uploadedFileName } = event.detail;

    if (this.printOption) {
      personalisation.providerUpdateEvent(
        this,
        { uploadedFileName },
        this.printOption.code,
      );
    }

    this.fileName = uploadedFileName;
    this.file = event.detail;

    this.goFurther();
  }

  private uploadDone(event: CustomEvent) {
    if (this.errorMessage && this.errorMessage.length > 0) {
      this.errorMessage = undefined;
    }

    if (this.printOption) {
      personalisation.providerUpdateEvent(
        this,
        { logoUuid: event.detail },
        this.printOption.code,
      );
    }

    this.goFurther();
  }

  private goToStep(event: CustomEvent<PersonalisationStepId>) {
    if (!event.detail) {
      this.goFurther();
      return;
    }
    // Step index is incremented by 2 to skip the colors slection step
    this.stepIndex = this.stepIndex + 2;
    this.stepSkipped = true;
  }

  private getTextContent(event: CustomEvent) {
    const { textPersonalization } = event.detail;

    if (this.printOption) {
      personalisation.providerUpdateEvent(
        this,
        { textPersonalization },
        this.printOption.code,
      );
    }

    this.goFurther();
  }

  private resetUpload(event: CustomEvent) {
    this.fileName = undefined;
    this.stepIndex = event.detail;
    return;
  }

  private uploadError(event: CustomEvent) {
    if (event.detail.error && typeof event.detail.error === 'string') {
      this.errorMessage = event.detail.error;
    }
    this.stepIndex = PersonalisationStepId.UPLOAD_SELECT_FILE;
  }

  private pmsColors(event: CustomEvent) {
    const { colors } = event.detail;

    if (this.printOption) {
      personalisation.providerUpdateEvent(
        this,
        { colors },
        this.printOption.code,
      );
    }

    this.goFurther();
  }

  private removePersonalisationData() {
    this.uploadService?.cancel();
    if (!this.printOption || this.flow[this.stepIndex] === STEP_DONE) {
      return;
    }

    personalisation.providerRemoveEvent(this, this.printOption.code);
  }

  private getStepContent(): TemplateResult {
    switch (this.getCurrentStep().id) {
      case PersonalisationStepId.TYPE:
        return html`
          <fg-personalisation-choose-type
            @personalisation-choose-type=${this.onSelectFlow}
          ></fg-personalisation-choose-type>
        `;
      case PersonalisationStepId.UPLOAD_SELECT_FILE:
        return html`
          <fg-upload-file @upload-file=${this.goToUpload}></fg-upload-file>
        `;
      case PersonalisationStepId.UPLOAD_PROCESS:
        return html`
          <fg-personalisation-uploading
            .file=${this.file}
            @personalisation-uploaded=${this.uploadDone}
            @upload-error=${this.uploadError}
          ></fg-personalisation-uploading>
        `;
      case PersonalisationStepId.COLORS_SOURCE:
        return html`
          <fg-personalisation-color-option-step
            .fileName=${this.fileName}
            .selectedFlow=${this.flow}
            @personalisation-proceed-step=${this.goToStep}
            @personalisation-reset-upload-file=${this.resetUpload}
          ></fg-personalisation-color-option-step>
        `;
      case PersonalisationStepId.PICK_COLORS:
        return html`
          <fg-personalisation-choose-colors-step
            .colorAmount=${this.printOption?.colorCount}
            @personalisation-colors=${this.pmsColors}
          ></fg-personalisation-choose-colors-step>
        `;
      case PersonalisationStepId.PICK_COLORS_WITH_FILE:
        return html`
          <fg-personalisation-choose-colors-step
            .fileName=${this.fileName}
            .colorAmount=${this.printOption?.colorCount}
            @personalisation-colors=${this.pmsColors}
            @personalisation-reset-upload-file=${this.resetUpload}
          ></fg-personalisation-choose-colors-step>
        `;
      case PersonalisationStepId.TEXT:
        return html`
          <fg-personalisation-text-step
            @personalisation-text-step=${this.getTextContent}
            .selectedFlow=${this.flow}
          ></fg-personalisation-text-step>
        `;
      case PersonalisationStepId.TEXT_WITH_FILE:
        return html`
          <fg-personalisation-text-step
            @personalisation-text-step=${this.getTextContent}
            .selectedFlow=${this.flow}
            .fileName=${this.fileName}
            @personalisation-reset-upload-file=${this.resetUpload}
          ></fg-personalisation-text-step>
        `;
      case PersonalisationStepId.DONE:
        return html`
          <fg-personalisation-step-done
            .fileName=${this.fileName}
            @personalisation-completed=${this.closeDialog}
          ></fg-personalisation-step-done>
        `;
    }
  }

  render() {
    return html`
      <fg-dialog
        ${ref(this.fgDialogRef)}
        @dialog-closed=${this.removePersonalisationData}
      >
        <span slot="title">${this.getStepTitle()}</span>
        <span slot="pre-body">${this.getBackButton()}</span>
        <div class="body">
          <div class="print-option-summary">
            <slot name="print-option-summary"></slot>
          </div>
          ${this.errorMessage
            ? html`
                <fg-personalisation-error
                  part=${this.globalContext.layout}
                  .errorMessage=${this.errorMessage}
                ></fg-personalisation-error>
              `
            : nothing}
          <div>${this.getStepContent()}</div>
        </div>
        <p slot="footer">${t('content.personalisation.freePrintProof')}</p>
      </fg-dialog>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    css`
      .body {
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        background: var(
          --fg-success-background,
          var(--fg-fallback-success-background)
        );
        padding: 15px 8px 8px;
      }

      button {
        color: var(--fg-primary, var(--fg-fallback-primary));
      }
      button:hover,
      button:focus {
        text-decoration: underline;
      }

      .previous-button {
        display: flex;
        align-items: center;
      }

      .previous-icon {
        height: 13px;
        width: 9px;
        margin-right: 2px;
      }

      p {
        margin-top: 20px;
      }

      .print-option-summary {
        margin-bottom: 15px;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        .body {
          padding-top: 12px;
        }
      }
    `,
  ];
}
